"use client";

import React, { useState, useEffect, useRef } from "react";
import { 
  X, 
  QrCode, 
  Download, 
  Copy, 
  Check, 
  ExternalLink, 
  ChefHat, 
  Sparkles, 
  UtensilsCrossed, 
  Layers,
  Printer 
} from "lucide-react";
import QRCode from "qrcode";

interface RestaurantQrGeneratorModalProps {
  isOpen: boolean;
  onClose: () => void;
  restaurantName?: string;
}

export default function RestaurantQrGeneratorModal({
  isOpen,
  onClose,
  restaurantName = "The Smoke Co.",
}: RestaurantQrGeneratorModalProps) {
  const [selectedTable, setSelectedTable] = useState("T-04");
  const [qrDataUrl, setQrDataUrl] = useState<string>("");
  const [copiedLink, setCopiedLink] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);

  // Derive target URL
  const [targetUrl, setTargetUrl] = useState("");

  useEffect(() => {
    if (typeof window !== "undefined") {
      const origin = window.location.origin;
      const url = `${origin}/demos/restaurant/table?table=${encodeURIComponent(selectedTable)}&restaurant=${encodeURIComponent(restaurantName)}`;
      setTargetUrl(url);

      QRCode.toDataURL(url, {
        width: 380,
        margin: 2,
        color: {
          dark: "#0F172A",
          light: "#FFFFFF",
        },
      })
        .then((dataUri) => setQrDataUrl(dataUri))
        .catch((err) => console.error("QR Code generation error:", err));
    }
  }, [selectedTable, restaurantName, isOpen]);

  if (!isOpen) return null;

  const handleCopyLink = () => {
    navigator.clipboard.writeText(targetUrl);
    setCopiedLink(true);
    setTimeout(() => setCopiedLink(false), 2500);
  };

  const handleDownloadQr = () => {
    const standImageUrl = `/api/demos/restaurant/vouchers/qr?table=${encodeURIComponent(selectedTable)}&restaurant=${encodeURIComponent(restaurantName)}&type=stand`;
    const a = document.createElement("a");
    a.href = standImageUrl;
    a.download = `${restaurantName.toLowerCase().replace(/\s+/g, "_")}_table_${selectedTable}_acrylic_stand.png`;
    a.target = "_blank";
    a.click();
  };

  const handlePrintTableTent = () => {
    if (!qrDataUrl) return;
    const printWindow = window.open("", "_blank");
    if (!printWindow) return;
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>${restaurantName} - Table Stand (${selectedTable})</title>
          <style>
            body { margin: 0; display: flex; justify-content: center; align-items: center; min-height: 100vh; background: #f8fafc; font-family: system-ui, -apple-system, sans-serif; }
            .card { width: 340px; background: #090d16; color: #fff; border: 3px solid #d97706; border-radius: 24px; padding: 28px 20px; text-align: center; box-shadow: 0 20px 25px -5px rgba(0,0,0,0.3); }
            .brand { font-family: Georgia, serif; font-size: 24px; color: #fbbf24; font-weight: bold; margin-bottom: 4px; }
            .subtitle { font-size: 10px; text-transform: uppercase; letter-spacing: 2px; color: #94a3b8; margin-bottom: 20px; }
            .qr-box { background: #fff; padding: 12px; border-radius: 16px; width: 200px; height: 200px; margin: 0 auto; box-sizing: border-box; }
            .qr-box img { width: 100%; height: 100%; }
            .offer-title { margin-top: 20px; font-size: 16px; font-weight: 800; color: #fbbf24; text-transform: uppercase; letter-spacing: 0.5px; }
            .offer-sub { font-size: 11px; color: #cbd5e1; margin-top: 4px; }
            .footer { margin-top: 20px; padding-top: 14px; border-top: 1px dashed #334155; display: flex; justify-content: space-between; font-size: 11px; color: #94a3b8; align-items: center; }
            .table-badge { font-weight: bold; color: #fbbf24; background: #451a03; padding: 3px 10px; border-radius: 6px; border: 1px solid #78350f; font-family: monospace; }
            @media print { body { background: #fff; } .card { box-shadow: none; border-color: #000; } }
          </style>
        </head>
        <body>
          <div class="card">
            <div class="brand">🍽️ ${restaurantName}</div>
            <div class="subtitle">VIP Table Rewards</div>
            <div class="qr-box">
              <img src="${qrDataUrl}" alt="Table QR" />
            </div>
            <div class="offer-title">SCAN FOR FREE APPETIZER</div>
            <div class="offer-sub">Point phone camera at QR code</div>
            <div class="footer">
              <span>Scan & Dine</span>
              <span class="table-badge">${selectedTable}</span>
            </div>
          </div>
          <script>window.onload = function() { window.print(); };</script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  const tableOptions = [
    "T-01", "T-02", "T-03", "T-04", "T-05", "T-06",
    "T-07", "T-08", "T-09", "T-10", "T-11", "T-12",
    "Bar-01", "Bar-02", "VIP-Booth-1", "Patio-Garden"
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-md animate-in fade-in duration-200">
      <div className="relative w-full max-w-xl bg-slate-900 border border-slate-800 rounded-2xl shadow-2xl p-6 text-slate-100 max-h-[92vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between pb-4 border-b border-slate-800">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl bg-amber-500/20 border border-amber-500/30 flex items-center justify-center text-amber-400">
              <QrCode className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-base font-bold text-white flex items-center gap-2">
                <span>Table QR Stand Generator</span>
                <span className="text-[10px] px-2 py-0.5 rounded bg-amber-500/20 text-amber-300 font-mono">
                  Live Vector
                </span>
              </h2>
              <p className="text-xs text-slate-400">
                Generate printable acrylic table-tent QRs for walk-in diner opt-ins.
              </p>
            </div>
          </div>

          <button
            onClick={onClose}
            className="p-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-400 hover:text-white transition"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 my-6 items-center">
          {/* Left: Table Selection */}
          <div>
            <label className="block text-xs font-semibold text-slate-300 uppercase tracking-wider mb-2">
              Select Table or Zone:
            </label>
            <div className="grid grid-cols-4 gap-2 mb-4">
              {tableOptions.map((tbl) => (
                <button
                  key={tbl}
                  type="button"
                  onClick={() => setSelectedTable(tbl)}
                  className={`py-2 px-1 rounded-lg text-xs font-semibold transition border ${
                    selectedTable === tbl
                      ? "bg-amber-500 text-slate-950 border-amber-400 font-bold shadow-md shadow-amber-500/20"
                      : "bg-slate-950 border-slate-800 text-slate-300 hover:bg-slate-800"
                  }`}
                >
                  {tbl}
                </button>
              ))}
            </div>

            <div className="p-3 rounded-xl bg-slate-950 border border-slate-800/80 text-xs space-y-2">
              <div className="flex justify-between text-slate-400">
                <span>Restaurant:</span>
                <span className="font-semibold text-white">{restaurantName}</span>
              </div>
              <div className="flex justify-between text-slate-400">
                <span>Offer Attached:</span>
                <span className="font-semibold text-amber-400">Complimentary Bruschetta</span>
              </div>
              <div className="flex justify-between text-slate-400">
                <span>Target Action:</span>
                <span className="font-semibold text-emerald-400">10s WhatsApp Opt-in</span>
              </div>
            </div>

            {/* Link preview and copy */}
            <div className="mt-4">
              <div className="text-[11px] text-slate-400 font-mono truncate mb-2 p-2 rounded bg-slate-950/60 border border-slate-800">
                {targetUrl || "Generating table link..."}
              </div>

              <div className="flex gap-2">
                <button
                  onClick={handleCopyLink}
                  className="flex-1 py-2 px-3 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-200 hover:text-white text-xs font-semibold flex items-center justify-center gap-1.5 transition"
                >
                  {copiedLink ? (
                    <>
                      <Check className="w-3.5 h-3.5 text-emerald-400" />
                      <span className="text-emerald-400">Copied!</span>
                    </>
                  ) : (
                    <>
                      <Copy className="w-3.5 h-3.5" />
                      <span>Copy Link</span>
                    </>
                  )}
                </button>

                <a
                  href={targetUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="py-2 px-3 rounded-lg bg-amber-500/20 hover:bg-amber-500/30 text-amber-300 border border-amber-500/30 text-xs font-semibold flex items-center justify-center gap-1 transition"
                >
                  <ExternalLink className="w-3.5 h-3.5" />
                  <span>Test Scan</span>
                </a>
              </div>
            </div>
          </div>

          {/* Right: Acrylic Table Tent Preview */}
          <div className="flex flex-col items-center">
            <div className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-2">
              Printable Acrylic Stand Preview
            </div>

            <div
              ref={cardRef}
              className="w-[230px] rounded-2xl bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 border-2 border-amber-500/40 p-4 shadow-2xl text-center relative overflow-hidden"
            >
              {/* Gold rim accent */}
              <div className="absolute top-0 inset-x-0 h-1 bg-gradient-to-r from-amber-500 via-amber-300 to-amber-500" />

              <div className="flex items-center justify-center gap-1.5 text-amber-400 text-xs font-bold font-serif mb-1">
                <ChefHat className="w-3.5 h-3.5" />
                <span>{restaurantName}</span>
              </div>

              <div className="text-[9px] uppercase tracking-widest text-slate-400 mb-2">
                VIP Table Rewards
              </div>

              {/* QR Image Box */}
              <div className="bg-white p-2.5 rounded-xl shadow-inner mx-auto w-[160px] h-[160px] flex items-center justify-center border border-slate-200">
                {qrDataUrl ? (
                  <img
                    src={qrDataUrl}
                    alt={`Table ${selectedTable} QR Code`}
                    className="w-full h-full object-contain"
                  />
                ) : (
                  <div className="w-6 h-6 border-2 border-slate-900 border-t-transparent rounded-full animate-spin" />
                )}
              </div>

              {/* Offer text */}
              <div className="mt-3">
                <div className="text-[11px] font-bold text-amber-300 uppercase tracking-tight">
                  Scan For Free Appetizer
                </div>
                <div className="text-[9px] text-slate-400 mt-0.5">
                  Point phone camera at QR code
                </div>
              </div>

              {/* Table Pill */}
              <div className="mt-3 pt-2 border-t border-slate-800/80 flex items-center justify-between text-[10px]">
                <span className="text-slate-500">Scan & Dine</span>
                <span className="font-mono font-bold text-amber-400 bg-amber-950/60 px-2 py-0.5 rounded border border-amber-800/40">
                  {selectedTable}
                </span>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col gap-2 mt-4 w-[230px]">
              <button
                onClick={handlePrintTableTent}
                className="w-full py-2 px-3 rounded-xl bg-amber-500 hover:bg-amber-400 text-slate-950 text-xs font-bold flex items-center justify-center gap-1.5 shadow-lg shadow-amber-500/20 transition"
              >
                <Printer className="w-3.5 h-3.5" />
                <span>Print Physical Stand</span>
              </button>

              <button
                onClick={handleDownloadQr}
                className="w-full py-1.5 px-3 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white text-xs font-semibold flex items-center justify-center gap-1.5 transition border border-slate-700"
              >
                <Download className="w-3.5 h-3.5" />
                <span>Download PNG</span>
              </button>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="pt-4 border-t border-slate-800 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-semibold transition"
          >
            Close Generator
          </button>
        </div>
      </div>
    </div>
  );
}
